// src/features/dashboard/components/EmployeeProductivity/ProductivitySummaryCards.tsx

import {
  BarChart3,
  Clock3,
  Users,
  Zap,
  CircleOff,
} from "lucide-react";

import {
  ProductivitySummaryCard,
} from "../../types/Employee/employeeProductivity.types";

interface Props {
  cards: ProductivitySummaryCard[];
}

const iconMap = {
  Zap,
  BarChart3,
  Clock3,
  CircleOff,
  Users,
};

const defaultConfig = {
  "Today's Productivity": {
    icon: "Zap",
    iconBg: "#E8F8F0",
    iconColor: "#18A957",
  },

  "Today Productivity": {
    icon: "Zap",
    iconBg: "#E8F8F0",
    iconColor: "#18A957",
  },

  "Monthly Avg": {
    icon: "BarChart3",
    iconBg: "#EAF3FF",
    iconColor: "#3B82F6",
  },

  "Monthly Average": {
    icon: "BarChart3",
    iconBg: "#EAF3FF",
    iconColor: "#3B82F6",
  },

  "Productive Time": {
    icon: "Clock3",
    iconBg: "#F3E8FF",
    iconColor: "#7C3AED",
  },

  "Active Time": {
    icon: "Clock3",
    iconBg: "#F3E8FF",
    iconColor: "#7C3AED",
  },

  "Non-Prod Time": {
    icon: "CircleOff",
    iconBg: "#FFF3E8",
    iconColor: "#F97316",
  },

  "Team Rank": {
    icon: "Users",
    iconBg: "#FCE7F3",
    iconColor: "#DB2777",
  },
} as const;


// ============================================================================
// FORMAT HOURS / MINUTES
// ============================================================================

const formatHoursMinutes = (
  value: string,
) => {

  if (!value) {
    return "-";
  }

  const [
    hours,
    minutes,
  ] = value
    .split(":")
    .map(Number);

  return `${hours}h ${minutes}m`;
};


// ============================================================================
// COMPONENT
// ============================================================================

const ProductivitySummaryCards = ({
  cards,
}: Props) => {

  return (

    <div
      className="
        grid
        w-full
        min-w-0

        /* ================================================================
           NORMAL
        ================================================================= */

        grid-cols-1
        gap-4

        md:grid-cols-2

        lg:grid-cols-5

        /* ================================================================
           110% ZOOM
           Keep all 5 cards in one row.
        ================================================================= */

        [@media(max-width:1100px)]:grid-cols-5
        [@media(max-width:1100px)]:gap-2

        /* ================================================================
           125% / 150% ZOOM
           Keep all 5 cards in one row and make everything smaller.
        ================================================================= */

        [@media(max-width:1000px)]:grid-cols-5
        [@media(max-width:1000px)]:gap-1.5

        /* ================================================================
           MOBILE ONLY
        ================================================================= */

        [@media(max-width:699px)]:grid-cols-1
        [@media(max-width:699px)]:gap-2.5
      "
    >

      {cards.map(
        (item) => {

          // ================================================================
          // EXISTING ACTIVE TIME LOGIC
          // ================================================================

          const card =
            item.title === "Active Time"
              ? {
                  ...item,

                  title:
                    "Non-Prod Time",

                  icon:
                    "CircleOff",

                  iconBg:
                    "#FFF3E8",

                  iconColor:
                    "#F97316",
                }
              : item;


          // ================================================================
          // DEFAULT CONFIG
          // ================================================================

          const config =
            defaultConfig[
              card.title as keyof typeof defaultConfig
            ];


          // ================================================================
          // ICON
          // ================================================================

          const iconName =
            card.icon ||
            config?.icon ||
            "Zap";

          const Icon =
            iconMap[
              iconName as keyof typeof iconMap
            ] ?? Zap;


          // ================================================================
          // CARD
          // ================================================================

          return (

            <div
              key={card.id}
              className="
                flex
                min-w-0
                w-full
                overflow-hidden

                items-center

                rounded-[18px]

                border
                border-[#D0D5DD]

                bg-white

                shadow-sm

                transition-shadow
                duration-200

                hover:shadow-sm

                /* ========================================================
                   NORMAL DESKTOP
                ========================================================= */

                h-[78px]
                gap-3
                px-4
                py-3

                /* ========================================================
                   110% ZOOM
                ========================================================= */

                [@media(max-width:1100px)]:h-[68px]
                [@media(max-width:1100px)]:gap-1.5
                [@media(max-width:1100px)]:rounded-[12px]
                [@media(max-width:1100px)]:px-2
                [@media(max-width:1100px)]:py-2

                /* ========================================================
                   125% / 150% ZOOM
                ========================================================= */

                [@media(max-width:1000px)]:h-[62px]
                [@media(max-width:1000px)]:gap-1
                [@media(max-width:1000px)]:rounded-[10px]
                [@media(max-width:1000px)]:px-1.5
                [@media(max-width:1000px)]:py-1.5

                /* ========================================================
                   MOBILE
                ========================================================= */

                [@media(max-width:699px)]:h-[74px]
                [@media(max-width:699px)]:gap-2.5
                [@media(max-width:699px)]:rounded-[14px]
                [@media(max-width:699px)]:px-3
                [@media(max-width:699px)]:py-2
              "
            >

              {/* ==========================================================
                  ICON
              =========================================================== */}

              <div
                className="
                  flex
                  h-9
                  w-9
                  shrink-0

                  items-center
                  justify-center

                  rounded-full

                  /* 110% */

                  [@media(max-width:1100px)]:h-7
                  [@media(max-width:1100px)]:w-7

                  /* 125% / 150% */

                  [@media(max-width:1000px)]:h-6
                  [@media(max-width:1000px)]:w-6

                  /* Mobile */

                  [@media(max-width:699px)]:h-8
                  [@media(max-width:699px)]:w-8
                "
                style={{
                  backgroundColor:
                    card.iconBg ||
                    config?.iconBg ||
                    "#E8F8F0",
                }}
              >

                <Icon
                  size={16}
                  strokeWidth={2}
                  className="
                    [@media(max-width:1100px)]:h-[13px]
                    [@media(max-width:1100px)]:w-[13px]

                    [@media(max-width:1000px)]:h-[11px]
                    [@media(max-width:1000px)]:w-[11px]

                    [@media(max-width:699px)]:h-[14px]
                    [@media(max-width:699px)]:w-[14px]
                  "
                  color={
                    card.iconColor ||
                    config?.iconColor ||
                    "#18A957"
                  }
                />

              </div>


              {/* ==========================================================
                  CONTENT
              =========================================================== */}

              <div
                className="
                  min-w-0
                  flex-1
                  overflow-hidden
                "
              >

                {/* ========================================================
                    TITLE
                ========================================================= */}

                <p
                  className="
                    min-w-0

                    overflow-hidden

                    text-[13px]
                    font-semibold
                    leading-4

                    text-[#101828]

                    /* Normal */

                    line-clamp-2

                    break-words

                    /* ====================================================
                       110%
                    ==================================================== */

                    [@media(max-width:1100px)]:text-[9px]
                    [@media(max-width:1100px)]:leading-[10px]

                    /* ====================================================
                       125% / 150%
                    ==================================================== */

                    [@media(max-width:1000px)]:text-[8px]
                    [@media(max-width:1000px)]:leading-[9px]

                    /* ====================================================
                       Mobile
                    ==================================================== */

                    [@media(max-width:699px)]:text-[11px]
                    [@media(max-width:699px)]:leading-3.5
                  "
                  title={card.title}
                >
                  {card.title}
                </p>


                {/* ========================================================
                    VALUE
                ========================================================= */}

                <h3
                  className="
                    mt-1

                    min-w-0

                    overflow-hidden
                    text-ellipsis
                    whitespace-nowrap

                    text-[16px]
                    font-bold
                    leading-[18px]

                    text-[#101828]

                    /* ====================================================
                       110%
                    ==================================================== */

                    [@media(max-width:1100px)]:mt-0
                    [@media(max-width:1100px)]:text-[12px]
                    [@media(max-width:1100px)]:leading-[13px]

                    /* ====================================================
                       125% / 150%
                    ==================================================== */

                    [@media(max-width:1000px)]:text-[10px]
                    [@media(max-width:1000px)]:leading-[11px]

                    /* ====================================================
                       Mobile
                    ==================================================== */

                    [@media(max-width:699px)]:text-[14px]
                    [@media(max-width:699px)]:leading-4
                  "
                  title={
                    card.title ===
                      "Productive Time" ||
                    card.title ===
                      "Non-Prod Time"
                      ? formatHoursMinutes(
                          card.value,
                        )
                      : String(
                          card.value,
                        )
                  }
                >
                  {
                    card.title ===
                      "Productive Time" ||
                    card.title ===
                      "Non-Prod Time"
                      ? formatHoursMinutes(
                          card.value,
                        )
                      : card.value
                  }
                </h3>


                {/* ========================================================
                    SUBTITLE
                ========================================================= */}

                {card.subtitle && (

                  <p
                    className={`
                      min-w-0

                      overflow-hidden

                      text-[11px]
                      leading-[13px]

                      /* Show maximum 2 lines */

                      line-clamp-2
                      break-words

                      /* ==================================================
                         110%
                      ================================================== */

                      [@media(max-width:1100px)]:mt-0
                      [@media(max-width:1100px)]:text-[7.5px]
                      [@media(max-width:1100px)]:leading-[9px]

                      /* ==================================================
                         125% / 150%
                      ================================================== */

                      [@media(max-width:1000px)]:text-[7px]
                      [@media(max-width:1000px)]:leading-[8px]

                      /* ==================================================
                         Mobile
                      ================================================== */

                      [@media(max-width:699px)]:text-[10px]
                      [@media(max-width:699px)]:leading-3

                      ${
                        card.title.includes(
                          "Productivity",
                        ) ||
                        card.title ===
                          "Team Rank"
                          ? "font-medium text-[#16A34A]"
                          : "text-[#667085]"
                      }
                    `}
                    title={
                      card.subtitle
                    }
                  >
                    {card.subtitle}
                  </p>

                )}

              </div>

            </div>
          );
        },
      )}

    </div>
  );
};

export default ProductivitySummaryCards;